import * as React from "react";
import * as Sentry from "@sentry/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { signOut } from "@/lib/auth-client";
import { wipeLocalState } from "@/lib/sign-out";
import { persister } from "@/lib/query-persister";

// Centralized sign-out flow. Navigating to /sign-out:
//   1. Unmounts every /_authed component (no more useQuery subscribers).
//   2. Cancels in-flight queries + clears caches via wipeLocalState.
//   3. Hits better-auth /sign-out (clears session + 2FA-pending cookies).
//   4. Hard-reloads to /sign-in so React state is fully discarded.
// Callers should `navigate({ to: "/sign-out" })` rather than calling signOut
// directly so the unmount-first ordering is preserved.
export const Route = createFileRoute("/sign-out")({
  component: SignOutPage,
});

function SignOutPage() {
  const { queryClient } = Route.useRouteContext() as { queryClient: QueryClient };
  const navigate = useNavigate();

  React.useEffect(() => {
    void (async () => {
      try {
        const userId = (() => {
          try {
            return sessionStorage.getItem("vroom.lastUserId") ?? "anonymous";
          } catch {
            return "anonymous";
          }
        })();
        await queryClient.cancelQueries();
        await wipeLocalState({
          userId,
          queryClient,
          persisterRemove: async () => {
            await persister.removeClient();
          },
        });
        await signOut().catch(() => {});
        Sentry.setUser(null);
      } catch {
        // swallow — we always want to land on /sign-in
      } finally {
        await navigate({ to: "/sign-in", replace: true });
      }
    })();
  }, [queryClient, navigate]);

  return (
    <main className="grid min-h-dvh place-items-center bg-surface text-ink-700">
      <p className="text-[13px]">Uitloggen…</p>
    </main>
  );
}
